import { NativeModules } from 'react-native';
import Sentry from 'utils/sentry';
const DeviceRisk = NativeModules.DeviceRisk;
const FILENAME = 'deviceRisk.ts';
/**
 * Starts the iovation device inspector so it can collect device signals
 * asynchronously in the background. Call this when the user enters a flow
 * that will end with a `getBlackbox()` call (e.g. tapping the CTA on the
 * credit-score onboarding screen) so the SDK has time to warm up before the
 * fingerprint is read at submit time.
 *
 * Safe to call multiple times. No-op if the native module is unavailable.
 *
 * Platform note: on Android this is REQUIRED — it passes the iovation
 * subscriber key into the SDK and getBlackbox returns "" until it runs. On
 * iOS the subscriber key is loaded from Info.plist (`IOVSubKey`) at app
 * launch, so getBlackbox works without init() — but calling it earlier in
 * the flow still produces a richer blackbox. Always call this early.
 *
 * TODO: remove @public tag once this is imported by the credit-score MR.
 * @public
 */
export const init = () => {
    if (!DeviceRisk) {
        Sentry.addBreadcrumb({
            category: 'device-risk',
            level: 'warning',
            message: 'DeviceRisk native module unavailable',
        });
        return;
    }
    DeviceRisk.init();
};
/**
 * Returns the iovation device fingerprint ("blackbox") to POST to the backend
 * with calls like `POST /credit-score/registration`.
 *
 * Must be preceded by a call to `init()` — typically at the start of the
 * flow — so the SDK has time to collect device signals before the fingerprint
 * is read here. Calling without a prior `init()`, or too soon after it, may
 * return a short/partial string.
 *
 * Returns an empty string if the native module is unavailable (e.g. running
 * in an environment without the SDK, such as Storybook).
 *
 */
export const getBlackbox = async () => {
    if (!DeviceRisk) {
        Sentry.addBreadcrumb({
            category: 'device-risk',
            level: 'warning',
            message: 'DeviceRisk native module unavailable',
        });
        return '';
    }
    try {
        const bb = await DeviceRisk.getBlackbox();
        if (!bb) {
            Sentry.logError({
                filename: FILENAME,
                message: 'getBlackbox returned empty string',
                fingerprint: ['device-risk-empty'],
            });
        }
        return bb;
    }
    catch (err) {
        Sentry.logError({
            filename: FILENAME,
            err,
            message: 'getBlackbox threw',
            fingerprint: ['device-risk-threw'],
        });
        return '';
    }
};
